import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ChargePointList } from 'src/swagger-gen/vnf-secured/models';
import {
  ConfiguredGraphCreate,
  ConfiguredGraphUpdate,
  DataTimeStepType,
  DrivableTagList,

  GraphTagGroupList,
  GraphTagList,
  GraphTagSearch,
  HistoricalDataGraphBody,
  NetworkList,
  NetworkSearchBody,
  OpcuaConfig,
  OpcuaConfigCreate,
  OpcuaConfigList,
  OpcuaConfigUpdate,
  Station,
  StationCreate,
  StationHealth,
  StationPagedList,
} from 'src/swagger-gen/web-secured/models';
import { ConfiguredGraph } from 'src/swagger-gen/web-secured/models/configured-graph';
import { ConfiguredGraphOrder } from 'src/swagger-gen/web-secured/models/configured-graph-order';
import { StationChartList } from 'src/swagger-gen/web-secured/models/station-chart-list';
import { CalculationFunction } from '../../../enums/stations/calculation-function';
import { ChargePointModel } from '../../../models/chargepoint/chargepoint-vnf';
import { BaseService } from '../../../services/base.service';
import { AuthService } from '../../../tinea-components/okta/auth/auth.service';
import { StationRepoModelMapper } from '../mapper/model/station.mapper';
import {keysToSnake} from '../../../shared/utils/common.utils';
import {SitesOperationService} from '../../../../swagger-gen/web-secured/services/sites-operation.service';

@Injectable({
  providedIn: 'root',
})
export class StationsRepo extends BaseService {
  baseUrl = environment.apiUrl;
  stationUrl = this.baseUrl + '/stations';
  customStationUrl = this.baseUrl + '/custom/stations';
  networkUrl = this.baseUrl + '/networks';

  // TODO: waiting for API from APIM
  readonly geoGouvApi = 'https://api-adresse.data.gouv.fr/search';
  readonly STATION_LIMIT = '200';

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    public authService: AuthService,
    public sitesOperationService: SitesOperationService
  ) {
    super();
  }

  getStationsList(
    offset?: number,
    limit?: number,
    params?: any[],
  ): Observable<StationPagedList> {
    const url = this.stationUrl + '/search';
    let requestParams: any;
    let requestString = this.pagination(offset, limit, this.STATION_LIMIT);
    let queryParams = '';
    if (params) {
      params.forEach((p: any) => {
        queryParams += '&' + p.key + '=' + p.value;
      });
    }
    requestString += queryParams;
    requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParams,
    };
    return this.http.post<StationPagedList>(url, {}, httpOptions).pipe(
      tap((response) => {
        // this.logger.log(`Get stations List from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Get Configured Graph List by uid
   * @param uid - station uid
   */
  getGraphList(uid: string, inDashboard?: boolean): Observable<ConfiguredGraph[]> {
    const url = `${this.stationUrl}/${uid}/graph/configured`;
    let requestString = '';
    if (inDashboard) {
      requestString = 'inDashboard=' + inDashboard;
    }
    const requestParam = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParam
    };

    return this.http
      .get<ConfiguredGraph[]>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }
  getGraph(uid: string, uidGraph: string): Observable<ConfiguredGraph> {
    const url = `${this.stationUrl}/${uid}/graph/configured/${uidGraph}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.get<ConfiguredGraph>(url, httpOptions).pipe(
      catchError(this.handleServerError)
    );
  }
  createGraph(uid: string, create: ConfiguredGraphCreate): Observable<any> {
    const url = `${this.stationUrl}/${uid}/graph/configured`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(url, create, httpOptions).pipe(
      catchError(this.handleServerError)
    );
  }
  updateGraph(uid: string, uidGraph: string, update: ConfiguredGraphUpdate): Observable<any> {
    const url = `${this.stationUrl}/${uid}/graph/configured/${uidGraph}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.put<any>(url, update, httpOptions).pipe(
      catchError(this.handleServerError)
    );
  }

  deleteGraph(uid: string, uidGraph: string): Observable<any> {
    const url = `${this.stationUrl}/${uid}/graph/configured/${uidGraph}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.delete<any>(url, httpOptions).pipe(
      catchError(this.handleServerError)
    );
  }
  orderGraph(uid: string, order: ConfiguredGraphOrder) {
    const url = `${this.stationUrl}/${uid}/graph/configured`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .patch<any>(url, order, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  getNetworkList(
    offset?: number,
    limit?: number,
    params?: any[],
    body?: NetworkSearchBody
  ): Observable<NetworkList> {
    const url = this.networkUrl + '/search';
    let requestParams: any;
    let requestString = this.pagination(offset, limit, this.STATION_LIMIT);
    let queryParams = '';
    if (params) {
      params.forEach((p: any) => {
        queryParams += '&' + p.key + '=' + p.value;
      });
    }
    requestString += queryParams;
    requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParams,
    };

    const bodyReq = body ? body : { filters: [] };
    return this.http.post<NetworkList>(url, bodyReq, httpOptions).pipe(
      tap((response) => {}), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Get station by uid
   * @param uid - station uid
   */
  getStation(uid: string): Observable<Station> {
    const url = `${this.stationUrl}/${uid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .get<Station>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  /**
   * Get station health by uid
   * @param uid - station uid
   */
  getStationHealth(uid: string): Observable<StationHealth> {
    const url = `${this.stationUrl}/${uid}/health`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
        .get<StationHealth>(url, httpOptions)
        .pipe(
            // We convert the camelCase object fields to snake_case, because production and integration do not return the same format.
            map(keysToSnake),
            catchError(this.handleServerError)
        );
  }

  /**
   * Get Station Tags for Graph purpose
   * @param uid - station uid
   */
  getStationTags(
    uid: string,
    offset: number,
    limit: number,
    params: any[],
    body: GraphTagSearch
  ): Observable<GraphTagList> {
    const url = `${this.stationUrl}/${uid}/graph/tags/search`;
    let requestParams: any;
    let requestString = this.pagination(offset, limit, this.STATION_LIMIT);
    let queryParams = '';
    if (params) {
      params.forEach((p: any) => {
        queryParams += '&' + p.key + '=' + p.value;
      });
    }
    requestString += queryParams;
    requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParams,
    };
    const bodyReq = body ? body : { filters: [] };
    return this.http.post<GraphTagList>(url, bodyReq, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get station tag from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Get Station Grouped Tags for Graph purpose
   * @param uid - station uid
   */
  getStationGroupedTags(
    uid: string,
    offset: number,
    limit: number,
    params: any[],
    body: any
  ): Observable<GraphTagGroupList> {
    const url = `${this.stationUrl}/${uid}/graph/tags/group`;
    let requestParams: any;
    let requestString = this.pagination(offset, limit, this.STATION_LIMIT);
    let queryParams = '';
    if (params) {
      params.forEach((p: any) => {
        queryParams += '&' + p.key + '=' + p.value;
      });
    }
    requestString += queryParams;
    requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParams,
    };
    const bodyReq = body ? body : { filters: [] };
    return this.http.post<GraphTagGroupList>(url, bodyReq, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get station tag from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  getStationGroupedTagSearch(
    uid: string,
    offset: number,
    limit: number,
    params: any[],
    body: GraphTagSearch
  ): Observable<GraphTagGroupList> {
    const url = `${this.stationUrl}/${uid}/graph/tags/group/search`;
    let requestParams: any;
    let requestString = this.pagination(offset, limit, this.STATION_LIMIT);
    let queryParams = '';
    if (params) {
      params.forEach((p: any) => {
        queryParams += '&' + p.key + '=' + p.value;
      });
    }
    requestString += queryParams;
    requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: requestParams,
    };
    const bodyReq = body ? body : { filters: [] };
    return this.http.post<GraphTagGroupList>(url, bodyReq, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get station tag from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  /**
   * Get station graph by id & tag
   * @param id - station uid
   * @param tagIds - tag list
   */
  getStationGraphData(
    id: string,
    tagIds: string[],
    startDateTime: string,
    endDateTime: string,
    stepType: DataTimeStepType,
    stepRange: number,
    calculation: CalculationFunction,
    withStat?: boolean
  ): Observable<any> {
    const url = `${this.stationUrl}/${id}/graph/historical`;
    const body: HistoricalDataGraphBody = { tagIds: tagIds };
    let requestString: string;
    requestString =
      'startDateTime=' + startDateTime + '&endDateTime=' + endDateTime;
    if (stepType) {
      requestString += '&dataTimeStepType=' + stepType;
    }
    if (stepRange) {
      requestString += '&dataTimeStepRange=' + stepRange;
    }
    if (calculation) {
      requestString += '&calculationFunction=' + calculation;
    }
    if (withStat) {
      requestString += '&withStats=' + withStat;
    }
    requestString += '&graphDataValueType=INITIAL_CORRECTED';
    const requestParams = new HttpParams({ fromString: requestString });
    const httpOptions: any = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      observe: 'response',
      params: requestParams,
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }
  getStatsTags(
    uid: string,
    tagIds: string[],
    startDateTime: string,
    endDateTime: string,
  ): Observable<any> {
    const url = `${this.stationUrl}/${uid}/graph/historical/stats`;
    const body: HistoricalDataGraphBody = { tagIds: tagIds };
    let requestString: string;
    requestString =
      'startDateTime=' + startDateTime + '&endDateTime=' + endDateTime;
      requestString += '&graphDataValueType=INITIAL_CORRECTED';
    const requestParams = new HttpParams({ fromString: requestString });
    const httpOptions: any = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      observe: 'response',
      params: requestParams,
    };
    return this.http
    .post<any>(url, body, httpOptions)
    .pipe(catchError(this.handleServerError));
  }

  exportHistoricalData(
    id: string,
    tagIds: Array<string>,
    startDateTime: string,
    endDateTime: string
  ): Observable<any> {
    const url = `${this.stationUrl}/${id}/data/historical/analogic/export`;
    let requestString: string;
    requestString =
      'startDateTime=' + startDateTime + '&endDateTime=' + endDateTime;
    const requestParams = new HttpParams({ fromString: requestString });

    const body = { tagIds: tagIds };

    return this.http
      .post<any>(url, body, {
        headers: this.httpHeaders(),
        responseType: 'blob' as 'json',
        observe: 'response',
        params: requestParams,
      })
      .pipe(
        tap((data) => {
          this.logger.trace(`Export historical data in service.`, data);
        }), // DEBUG
        catchError(this.handleServerError)
      );
  }

  exportRequestData(
    tagIds: Array<string>,
    emails: Array<string>,
    endDateTime: string,
    startDateTime: string,
    dataType: any
  ): Observable<any> {
    const url = `${this.baseUrl}/data/exports/request`;
    console.log(url);
    const body: any = {
      tagIds: tagIds,
      emails: emails,
      endDate: endDateTime,
      startDate: startDateTime,
      dataType: dataType,
    };
    return this.http
      .post<any>(url, body, {
        headers: this.httpHeaders(),
      })
      .pipe(
        tap((data) => {
          this.logger.trace(`Export request data in service.`, data);
        }), // DEBUG
        catchError(this.handleServerError)
      );
  }

  createGenericStation(stationCreate: StationCreate): Observable<any> {
    const url = this.stationUrl;

    const httpOptions = {
      headers: this.httpHeaders(),
      observe: 'response' as 'body',
    };

    return this.http.post<any>(url, stationCreate, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`New station created`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  updateStation(stationUid: string, stationUpdate: any): Observable<any> {
    const url = `${this.stationUrl}/${stationUid}`;

    const httpOptions = {
      headers: this.httpHeaders(),
      observe: 'response' as 'body',
    };

    return this.http.put<void>(url, stationUpdate, httpOptions).pipe(
      tap((response) => {
        this.logger.log(
          `Station with uid:` + stationUid + ' updated',
          response
        );
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  listOpcuaConfigurations(eUid: string): Observable<OpcuaConfigList> {
    const url = `${this.stationUrl}/${eUid}/opcua`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.get<OpcuaConfigList>(url, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Get OPCUA config from backend`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  listGirConfigurations(eUid: string): Observable<any> {
    const url = `${this.stationUrl}/${eUid}/gir`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  createConfigurationOpcua(
    eUid: string,
    body: OpcuaConfigCreate
  ): Observable<OpcuaConfig> {
    const url = `${this.stationUrl}/${eUid}/opcua`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<OpcuaConfig>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Updated succesfully OPCUA config`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  deleteConfigurationOpcua(
    eUid: string,
    opcuaUid: string
  ): Observable<OpcuaConfig> {
    const url = `${this.stationUrl}/${eUid}/opcua/${opcuaUid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .delete<OpcuaConfig>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  deleteConfigurationGir(eUid: string, girUid: string): Observable<any> {
    const url = `${this.stationUrl}/${eUid}/gir/${girUid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .delete<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  createConfigurationGir(eUid: string, body: any): Observable<any> {
    const url = `${this.stationUrl}/${eUid}/gir`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  updateConfigurationOpcua(
    eUid: string,
    opcuaUid: string,
    body: OpcuaConfigUpdate
  ): Observable<OpcuaConfig> {
    const url = `${this.stationUrl}/${eUid}/opcua/${opcuaUid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.put<OpcuaConfig>(url, body, httpOptions).pipe(
      tap((response) => {
        this.logger.log(`Updated succesfully OPCUA config`, response);
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  updateConfigurationGir(
    eUid: string,
    girUid: string,
    body: any
  ): Observable<any> {
    const url = `${this.stationUrl}/${eUid}/gir/${girUid}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .put<any>(url, body, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  deleteStation(stationUid: string): Observable<any> {
    const url = `${this.stationUrl}/${stationUid}`;

    const httpOptions = {
      headers: this.httpHeaders(),
    };

    return this.http.delete<any>(url, httpOptions).pipe(
      tap((response) => {
        this.logger.log(
          `station with uid:` + stationUid + ' deleted',
          response
        );
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  exportConfigTableExport(
    eUid: string,
    opcuaUid: string,
    gnl: boolean
  ): Observable<any> {
    let url = '';
    url = `${this.stationUrl}/${eUid}/opcua/configuration/${opcuaUid}/export`;


    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http.get<any>(url, httpOptions).pipe(
      tap((response) => {
        this.logger.log(
          `Get configuration table for station ${eUid} from backend`,
          response
        );
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  importConfigTableExport(
    eUid: string,
    opcuaUid: string,
    file: any,
    gnl: boolean
  ): Observable<any> {
    let url = '';
    url = `${this.stationUrl}/${eUid}/opcua/configuration/${opcuaUid}/import`;


    const httpOptions: any = {
      headers: new HttpHeaders().set(
        'Content-Type',
        'application/vnd.ms-excel'
      ),
      observe: 'response',
    };

    return this.http.post<any>(url, file, httpOptions).pipe(
      tap((response) => {
        this.logger.log(
          `Got response for importing configuration table for station ${eUid} from backend`,
          response
        );
      }), // DEBUG
      catchError(this.handleServerError)
    );
  }

  getAddress(address: string): Observable<any> {
    const requestUrl = `${this.geoGouvApi}/?q=${address}`;

    return this.http
      .get<any[]>(requestUrl)
      .pipe(catchError(this.handleServerError));
  }

  exportStationDefinition(): Observable<any> {
    const url = `${this.stationUrl}/config/export`;

    const httpOptions: any = {
      headers: this.httpHeaders(),
      responseType: 'blob' as 'json',
      observe: 'response',
    };

    return this.http
      .get<any>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  importStationDefinition(file: any): Observable<any> {
    const url = `${this.stationUrl}/config/import`;

    const httpOptions: any = {
      headers: new HttpHeaders().set(
        'Content-Type',
        'application/vnd.ms-excel'
      ),
      observe: 'response',
    };

    return this.http
      .post<any>(url, file, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  getStationCharts(uid: string): Observable<StationChartList> {
    const url = `${this.stationUrl}/${uid}/charts`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .get<StationChartList>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  getDrivableTags(
    uid: string,
    offset?: number,
    limit?: number
  ): Observable<DrivableTagList> {
    const url = `${this.stationUrl}/${uid}/drive/tags`;

    let requestParams: any;
    const requestString = this.pagination(offset, limit, this.STATION_LIMIT);
    requestParams = new HttpParams({ fromString: requestString });
    const httpOptions = {
      headers: this.httpHeaders(),
      params: requestParams,
    };

    return this.http
      .get<DrivableTagList>(url, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  setTagValue(stationUid: string, tagUid: string, value: string) {
    const url = this.stationUrl + '/' + stationUid + '/drive/' + tagUid;
    let requestString;
    requestString = 'value=' + value;
    const requestParams = new HttpParams({ fromString: requestString });

    const httpOptions = {
      headers: this.httpHeaders(),
      params: requestParams,
    };
    return this.http
      .post(url, null, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  getChargePointsVnf(uid: string): Observable<ChargePointModel[]> {
    const url = `${this.customStationUrl}/${uid}/chargepoints`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.get<ChargePointList>(url, httpOptions)
                    .pipe(
                      map(StationRepoModelMapper.toChargePointModelList),
                      catchError(this.handleServerError)
                    );
  }
}
